/**
 * Creates a standard Vuex action that wraps a repository call in a Promise.
 *
 * Usage:
 *   actions: {
 *     getAllInbound: makeAction(repo, 'getallinbound', 'ALL_INBOUND'),
 *   }
 *
 * @param {object}      repo         The API repository module
 * @param {string}      repoMethod   Name of the repository method to call
 * @param {string|null} commitName   Mutation to commit on success, or null for no commit
 * @param {boolean}     checkStatus  If true (default), only resolve when status === 200
 * @returns {Function}  Vuex action function
 */
export function makeAction(repo, repoMethod, commitName = null, checkStatus = true) {
  return ({ commit }, payload) =>
    new Promise((resolve, reject) => {
      repo[repoMethod](payload)
        .then((response) => {
          if (!checkStatus || response.status === 200) {
            resolve(response);
            if (commitName) commit(commitName, response.data);
          }
        })
        .catch(reject);
    });
}

/**
 * Generates a full Vuex store module for a standard CRUD entity.
 *
 * The factory creates state, getters, actions, and mutations for these
 * standard operations: getAll, view, addNew, edit, receive, approve,
 * reject, delete, export.
 *
 * Extra state, getters, actions, and mutations can be merged in via
 * the `extra` parameter.
 *
 * @param {object} config
 * @param {string}  config.entity    Entity name in camelCase (e.g. 'inbound')
 * @param {object}  config.repo      The API repository module
 * @param {object}  [config.methods] Override repository method names per action
 * @param {object}  [config.extra]   Additional { state, getters, actions, mutations }
 *
 * @example
 * import repo from '@/api/modules/Operations/Outbound';
 * export default createCrudStoreModule({ entity: 'outbound', repo });
 */
export function createCrudStoreModule({ entity, repo, methods = {}, extra = {} }) {
  const E = entity.toUpperCase();
  const E_pascal = entity.charAt(0).toUpperCase() + entity.slice(1);

  const defaultMethods = {
    getAll: `getall${entity}`,
    view: `view${entity}`,
    addNew: `addnew${entity}`,
    edit: `edit${entity}`,
    receive: `receive${entity}`,
    approve: `approve${entity}`,
    reject: `reject${entity}`,
    delete: `delete${entity}`,
    export: `export${entity}`,
  };

  const m = { ...defaultMethods, ...methods };

  const stateKeys = {
    getAll: `get_all_${entity}`,
    view: `view_${entity}`,
    addNew: `add_new_${entity}`,
    edit: `edit_${entity}`,
    receive: `receive_${entity}`,
    approve: `approve_${entity}`,
    reject: `reject_${entity}`,
    delete: `delete_${entity}`,
    export: `export_${entity}`,
  };

  const mutationNames = {
    getAll: `ALL_${E}`,
    view: `V_${E}`,
    addNew: `NEW_${E}`,
    edit: `E_${E}`,
    receive: `REC_${E}`,
    approve: `APP_${E}`,
    reject: `REJ_${E}`,
    delete: `DEL_${E}`,
    export: `EXP_${E}`,
  };

  const baseState = Object.fromEntries(Object.entries(stateKeys).map(([, key]) => [key, []]));

  const baseGetters = {
    [`GET_ALL_${E}`]: (state) => state[stateKeys.getAll],
    [`VIEW_${E}`]: (state) => state[stateKeys.view],
    [`ADD_NEW_${E}`]: (state) => state[stateKeys.addNew],
    [`EDIT_${E}`]: (state) => state[stateKeys.edit],
    [`RECEIVE_${E}`]: (state) => state[stateKeys.receive],
    [`APPROVE_${E}`]: (state) => state[stateKeys.approve],
    [`REJECT_${E}`]: (state) => state[stateKeys.reject],
    [`DELETE_${E}`]: (state) => state[stateKeys.delete],
    [`EXPORT_${E}`]: (state) => state[stateKeys.export],
  };

  const baseActions = {};
  for (const [op, repoMethod] of Object.entries(m)) {
    if (typeof repo[repoMethod] !== "function") continue;
    baseActions[`${op === "getAll" ? "getAll" : op}${op === "getAll" ? E_pascal : E_pascal}`] = makeAction(
      repo,
      repoMethod,
      mutationNames[op]
    );
  }

  // Fix action names to match conventional Vuex naming
  const namedActions = {
    [`getAll${E_pascal}`]: makeAction(repo, m.getAll, mutationNames.getAll),
    [`view${E_pascal}`]: makeAction(repo, m.view, mutationNames.view),
    [`addNew${E_pascal}`]: makeAction(repo, m.addNew, mutationNames.addNew),
    [`edit${E_pascal}`]: makeAction(repo, m.edit, mutationNames.edit),
    [`receive${E_pascal}`]: makeAction(repo, m.receive, mutationNames.receive),
    [`approve${E_pascal}`]: makeAction(repo, m.approve, mutationNames.approve),
    [`reject${E_pascal}`]: makeAction(repo, m.reject, mutationNames.reject),
    [`delete${E_pascal}`]: makeAction(repo, m.delete, mutationNames.delete),
    [`export${E_pascal}`]: makeAction(repo, m.export, mutationNames.export),
  };

  // Remove actions for non-existent repository methods
  for (const [op, repoMethod] of Object.entries(m)) {
    if (typeof repo[repoMethod] !== "function") {
      const E_pascal_key = `${op.charAt(0).toUpperCase() + op.slice(1)}${E_pascal}`;
      // build the action name the same way
      const actionKey = op === "getAll" ? `getAll${E_pascal}` : `${op}${E_pascal}`;
      delete namedActions[actionKey];
    }
  }

  const baseMutations = {
    [mutationNames.getAll]: (state, data) => {
      state[stateKeys.getAll] = data;
    },
    [mutationNames.view]: (state, data) => {
      state[stateKeys.view] = data;
    },
    [mutationNames.addNew]: (state, data) => {
      state[stateKeys.addNew] = data;
    },
    [mutationNames.edit]: (state, data) => {
      state[stateKeys.edit] = data;
    },
    [mutationNames.receive]: (state, data) => {
      state[stateKeys.receive] = data;
    },
    [mutationNames.approve]: (state, data) => {
      state[stateKeys.approve] = data;
    },
    [mutationNames.reject]: (state, data) => {
      state[stateKeys.reject] = data;
    },
    [mutationNames.delete]: (state, data) => {
      state[stateKeys.delete] = data;
    },
    [mutationNames.export]: (state, data) => {
      state[stateKeys.export] = data;
    },
  };

  return {
    state: { ...baseState, ...(extra.state ?? {}) },
    getters: { ...baseGetters, ...(extra.getters ?? {}) },
    actions: { ...namedActions, ...(extra.actions ?? {}) },
    mutations: { ...baseMutations, ...(extra.mutations ?? {}) },
  };
}
